import { createApi } from '@reduxjs/toolkit/query/react';
import baseApi from './base';
import { setBreadcrumbs } from 'slices/config';
import { INTERVIEW_TYPE } from 'constants';
import { envConfig } from 'config';
import paginationParams from 'utils/pagination-params';
import { EXTERNAL_ASSESSMENT_TYPE } from 'constants';

export const list = 'LIST_APPLICANTS';
export const single = 'SINGLE_APPLICANT';

export const listInterviews = 'LIST_APPLICANT_INTERVIEWS';
export const listResumes = 'LIST_APPLICANT_RESUMES';
export const listAssessments = 'LIST_APPLICANT_ASSESSMENTS';
export const singleInterview = 'SINGLE_APPLICANT_INTERVIEW';

export const applicantsApi = createApi({
  reducerPath: 'applicantsApi',
  baseQuery: baseApi,
  tagTypes: [list, single],
  endpoints: (builder) => ({
    createApplicant: builder.mutation({
      query: (applicant) => ({
        url: '/applicants/create',
        method: 'POST',
        body: applicant
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data: created } = await queryFulfilled;
          dispatch(
            applicantsApi.util.updateQueryData('listApplicants', undefined, (draft) => {
              draft.data.items.unshift(created.data);
            })
          );
        } catch (err) {
          console.error('Error updating cache:', err);
        }
      }
    }),
    updateApplicant: builder.mutation({
      query: ({ applicantId, ...attributes }) => ({
        url: `/applicants/update/${applicantId}`,
        method: 'POST',
        body: { ...attributes }
      }),
      invalidatesTags: [single],
      async onQueryStarted({ applicantId }, { dispatch, queryFulfilled }) {
        try {
          const { data: updated } = await queryFulfilled;
          dispatch(
            applicantsApi.util.updateQueryData('listApplicants', undefined, (draft) => {
              const applicant = draft.data.items.find((applicant) => applicant.id === applicantId);
              if (applicant) {
                Object.assign(applicant, updated.data);
              }
            })
          );
        } catch (err) {
          console.error('Error updating cache:', err);
        }
      }
    }),
    deleteApplicant: builder.mutation({
      query: (applicantId) => ({
        url: `/applicants/delete/${applicantId}`,
        method: 'DELETE'
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            applicantsApi.util.updateQueryData('listApplicants', undefined, (draft) => {
              const idx = draft.data.items.findIndex((item) => item.id === id);
              if (idx > -1) {
                draft.data.items.splice(idx, 1);
              }
            })
          );
        } catch (err) {
          console.error('Error updating cache:', err);
        }
      }
    }),
    unarchiveApplicant: builder.mutation({
      query: (ids) => ({
        url: `/applicants/unarchive`,
        method: 'POST',
        body: {
          ids
        }
      }),
      async onQueryStarted(ids, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            applicantsApi.util.updateQueryData('listApplicants', undefined, (draft) => {
              draft.data.items = draft.data.items.filter((item) => !ids.includes(item.id));
            })
          );
        } catch (err) {
          console.error('Error updating cache:', err);
        }
      }
    }),
    getApplicantById: builder.query({
      query: (applicantId) => `/applicants/get/${applicantId}`,
      providesTags: [single]
    }),
    listApplicants: builder.query({
      query: (params) => ({
        url: '/applicants/list',
        params
      }),
      providesTags: [list],
      ...paginationParams
    }),
    sendLanguageAssessment: builder.mutation({
      query: (applicant) => ({
        url: '/applicant-interviews/send-assessment',
        method: 'POST',
        body: { type: EXTERNAL_ASSESSMENT_TYPE.LANGUAGE, ...applicant }
      })
    }),
    sendWritingAssessment: builder.mutation({
      query: (applicant) => ({
        url: '/applicant-interviews/send-assessment',
        method: 'POST',
        body: { type: EXTERNAL_ASSESSMENT_TYPE.WRITING, ...applicant }
      })
    }),
    sendANps: builder.mutation({
      query: (applicant) => ({
        url: '/applicant-interviews/send-anps-survey',
        method: 'POST',
        body: applicant
      })
    }),
    sendVideos: builder.mutation({
      query: (applicant) => ({
        url: '/applicant-interviews-group/create',
        method: 'POST',
        body: {
          type: INTERVIEW_TYPE.VIDEO_QUESTION,
          ...applicant
        }
      }),
      async onQueryStarted({ applicantId, referenceIds }, { dispatch, queryFulfilled }) {
        const update = dispatch(
          applicantsApi.util.updateQueryData('listApplicants', undefined, (draft) => {
            const applicant = draft.data.items.find((applicant) => applicant.id === applicantId);
            if (applicant) {
              Object.assign(applicant, {
                results: referenceIds?.reduce((acc, id) => {
                  acc[id] = 'new';
                  return acc;
                }, {})
              });
            }
          })
        );
        try {
          await queryFulfilled;
        } catch (err) {
          update.undo();
          console.error('Error updating cache:', err);
        }
      }
    }),
    sendResume: builder.mutation({
      query: (applicant) => ({
        url: '/applicant-interviews/create',
        method: 'POST',
        body: { type: INTERVIEW_TYPE.RESUME_PROFILE, ...applicant }
      })
    }),
    sendAssessment: builder.mutation({
      query: (applicant) => ({
        url: '/applicant-interviews/create',
        method: 'POST',
        body: { type: INTERVIEW_TYPE.TYPING_ASSESSMENT, ...applicant }
      }),
      invalidatesTags: [listAssessments]
    }),
    listApplicantInterviews: builder.query({
      query: (params) => ({
        url: '/applicant-interviews/list',
        params: { type: INTERVIEW_TYPE.VIDEO_QUESTION, ...params }
      }),
      providesTags: [listInterviews],
      ...paginationParams
    }),
    exportApplicantInterviews: builder.query({
      query: (params) => ({
        url: '/applicant-interviews/export',
        params: { ...params },
        responseHandler: (response) => response.text()
      })
    }),
    exportApplicants: builder.query({
      query: (params) => ({
        url: '/applicants/export',
        params: { ...params },
        responseHandler: (response) => response.text()
      })
    }),
    unarchiveApplicantInterview: builder.mutation({
      query: (ids) => ({
        url: `/applicant-interviews/unarchive`,
        method: 'POST',
        body: {
          ids
        }
      }),
      async onQueryStarted(ids, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            applicantsApi.util.updateQueryData('listApplicantInterviews', undefined, (draft) => {
              draft.data.items = draft.data.items.filter((item) => !ids.includes(item.id));
            })
          );
        } catch (err) {
          console.error('Error updating cache:', err);
        }
      }
    }),
    listApplicantResumes: builder.query({
      query: (params) => ({
        url: '/applicant-interviews/list',
        params: { type: INTERVIEW_TYPE.RESUME_PROFILE, ...params }
      }),
      providesTags: [listResumes],
      ...paginationParams
    }),
    unarchiveApplicantResume: builder.mutation({
      query: (ids) => ({
        url: `/applicant-interviews/unarchive`,
        method: 'POST',
        body: {
          ids
        }
      }),
      async onQueryStarted(ids, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            applicantsApi.util.updateQueryData('listApplicantResumes', undefined, (draft) => {
              draft.data.items = draft.data.items.filter((item) => !ids.includes(item.id));
            })
          );
        } catch (err) {
          console.error('Error updating cache:', err);
        }
      }
    }),
    listApplicantAssessments: builder.query({
      query: (params) => ({
        url: '/applicant-interviews/list',
        params: { type: INTERVIEW_TYPE.TYPING_ASSESSMENT, ...params }
      }),
      providesTags: [listAssessments],
      ...paginationParams
    }),
    unarchiveApplicantAssessment: builder.mutation({
      query: (ids) => ({
        url: `/applicant-interviews/unarchive`,
        method: 'POST',
        body: {
          ids
        }
      }),
      async onQueryStarted(ids, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            applicantsApi.util.updateQueryData('listApplicantAssessments', undefined, (draft) => {
              draft.data.items = draft.data.items.filter((item) => !ids.includes(item.id));
            })
          );
        } catch (err) {
          console.error('Error updating cache:', err);
        }
      }
    }),
    getApplicantInterview: builder.query({
      query: ({ id, archived }) => ({ url: `/applicant-interviews/view/${id}`, ...(archived && { params: { archived: 1 } }) }),
      providesTags: [singleInterview],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data?.data?.status === 'evaluated') {
            dispatch(
              setBreadcrumbs(
                `${data?.data?.applicantName ? data?.data?.applicantName + '/' : ''}${data?.data?.referenceName ?? 'Interview Result'}`
              )
            );
          }
        } catch (err) {
          console.error('Error updating cache:', err);
        }
      }
    }),
    getApplicantInterviewGroup: builder.query({
      query: ({ id, index }) => ({ url: `/applicant-interviews-group/view/${id}`, params: { index } }),
      providesTags: [singleInterview],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data?.data?.status === 'evaluated') {
            dispatch(
              setBreadcrumbs(
                `${data?.data?.applicantName ? data?.data?.applicantName + '/' : ''}${data?.data?.referenceName ?? 'Interview Result'}`
              )
            );
          }
        } catch (err) {
          console.error('Error updating cache:', err);
        }
      }
    }),
    submitApplicantInterview: builder.mutation({
      query: ({ id, ...interview }) => ({
        url: `/applicant-interviews/submit/${id}`,
        method: 'PUT',
        body: interview
      }),
      invalidatesTags: (result) => {
        return result.status ? [singleInterview] : [];
      }
    }),
    analyzeResume: builder.mutation({
      query: (s3Key) => ({
        url: `${envConfig.streamLambdaUrl}/applicant-interviews/parse-resume`,
        method: 'POST',
        body: {
          s3Key
        }
      })
    }),
    startAssessment: builder.mutation({
      query: ({ id }) => ({
        url: `/applicant-interviews/start/${id}`,
        method: 'PUT'
      })
    }),
    abandonAssessment: builder.mutation({
      query: ({ id }) => ({
        url: `/applicant-interviews/abandon/${id}`,
        method: 'PUT'
      })
    })
  })
});

export const {
  useCreateApplicantMutation,
  useUpdateApplicantMutation,
  useDeleteApplicantMutation,
  useUnarchiveApplicantMutation,
  useGetApplicantByIdQuery,
  useListApplicantsQuery,
  useSendLanguageAssessmentMutation,
  useSendWritingAssessmentMutation,
  useSendANpsMutation,
  useSendVideosMutation,
  useSendResumeMutation,
  useSendAssessmentMutation,
  useListApplicantInterviewsQuery,
  useLazyExportApplicantInterviewsQuery,
  useExportApplicantsQuery,
  useLazyExportApplicantsQuery,
  useUnarchiveApplicantInterviewMutation,
  useListApplicantResumesQuery,
  useUnarchiveApplicantResumeMutation,
  useListApplicantAssessmentsQuery,
  useUnarchiveApplicantAssessmentMutation,
  useGetApplicantInterviewQuery,
  useGetApplicantInterviewGroupQuery,
  useSubmitApplicantInterviewMutation,
  useAnalyzeResumeMutation,
  useStartAssessmentMutation,
  useAbandonAssessmentMutation
} = applicantsApi;
